import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from 'ui/drawer'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { config } from '~/config/env'
import { useSettingsStore } from '~/stores/settings'
import { getJwtToken } from '~/utils/get-jwt-token'
import { setupNotifications } from '~/utils/setup-notifications'

import { PushPermissionBlockedDrawer } from './push-permission-blocked-drawer'

export const PushDailyWordPrompt = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [blockedOpen, setBlockedOpen] = useState(false)

  const endpoint = useSettingsStore(state => state.pushSubscriptionEndpoint)
  const isDisplayed = useSettingsStore(state => state.isPushDailyWordPromptDisplayed)
  const pushNotificationsEnabled = useSettingsStore(state => state.pushNotificationsEnabled)
  const patchSettings = useSettingsStore(state => state.patchSettings)

  const skipRender = !getJwtToken() || !!endpoint || isDisplayed || pushNotificationsEnabled

  useEffect(() => {
    if (skipRender) return

    const timer = setTimeout(() => setIsOpen(true), config.VITE_PUSH_PROMPT_DELAY_MINUTES * 60 * 1000)

    return () => clearTimeout(timer)
  }, [skipRender])

  const markDisplayed = () => patchSettings({ isPushDailyWordPromptDisplayed: true })

  const onEnable = async () => {
    const result = await setupNotifications()
    if (!result.ok) {
      markDisplayed()
      setIsOpen(false)
      if (result.reason === 'was-denied') setBlockedOpen(true)

      return
    }

    patchSettings({
      pushSubscriptionEndpoint: result.endpoint,
      isPushDailyWordPromptDisplayed: true,
      pushNotificationsEnabled: true,
    })
    setIsOpen(false)

    try {
      await apiClient.POST('/push/toggle-subscription', {
        body: { endpoint: result.endpoint, enabled: true },
      })
    } catch (error) {
      toast.error(t('Network error'))
      console.error({ message: 'Network error', error })
      return
    }
  }

  const onNotNow = () => {
    markDisplayed()
    setIsOpen(false)
  }

  if (skipRender) return null

  return (
    <>
      <Drawer
        onOpenChange={nextOpen => {
          if (!nextOpen) onNotNow()
        }}
        open={isOpen}
      >
        <DrawerContent>
          <div className="mx-auto w-full max-w-sm">
            <DrawerHeader>
              <DrawerTitle>{t('Daily word')}</DrawerTitle>
              <DrawerDescription>{t('Turn on notifications to get a new word every day')}</DrawerDescription>
            </DrawerHeader>
            <DrawerFooter className="p-4">
              <Button onClick={onEnable}>{t('Enable notifications')}</Button>
              <DrawerClose render={<Button variant="outline" />}>{t('Not now')}</DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
      <PushPermissionBlockedDrawer onOpenChange={setBlockedOpen} open={blockedOpen} />
    </>
  )
}
